import { Box, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import RecordCounter from "@entrilia/shared/components/filters/RecordCounter";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import type {
  InvestorCommunicationReader,
  InvestorCommunicationRecipient,
  InvestorCommunicationType,
} from "../../../api/types/investorCommunicationsTypes";
import InvestorCommunicationRecipientsGrid from "./InvestorCommunicationRecipientsGrid";

const applySearchFilter = (searchValue: string, recipients: InvestorCommunicationRecipient[]) => {
  const searchTerm = searchValue.toLowerCase().trim();
  if (searchTerm.length === 0) {
    return recipients;
  }

  return recipients.filter(
    (recipient) =>
      recipient.investorName.toLowerCase().includes(searchTerm) ||
      recipient.contactName.toLowerCase().includes(searchTerm) ||
      (recipient.contactEmail ?? "").toLowerCase().includes(searchTerm)
  );
};

interface Props {
  type: InvestorCommunicationType;
  recipients: InvestorCommunicationRecipient[];
  readersByContactId?: Map<string, InvestorCommunicationReader>;
}

const InvestorCommunicationRecipientsList = ({ type, recipients, readersByContactId }: Props) => {
  const [searchValue, setSearchValue] = useState("");

  const filteredRecipients = useMemo(() => applySearchFilter(searchValue, recipients), [searchValue, recipients]);

  return (
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <RecordCounter records={filteredRecipients.length} totalRecords={recipients.length} />
        <SearchField
          initialValue={searchValue}
          onSearch={setSearchValue}
          debounceTimeMs={300}
          disabled={recipients.length === 0}
        />
      </Box>
      <InvestorCommunicationRecipientsGrid
        type={type}
        recipients={filteredRecipients}
        readersByContactId={readersByContactId}
      />
    </Stack>
  );
};

export default InvestorCommunicationRecipientsList;
